import { LinkOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Descriptions, Empty, Form, Input, notification, Tag, Tooltip, Typography } from 'antd'
import type { JSX } from 'react'

import { CommunityRegistration, linkTCRBrand } from 'api/tcr'
import { QUERIES } from 'constants/queries'

type LinkBrandProps = {
  brand: CommunityRegistration['brand'] | undefined
  canWrite?: boolean
  clientId: string
  disabledTooltip?: string
}

export function LinkBrand({ brand, canWrite = true, clientId, disabledTooltip }: LinkBrandProps): JSX.Element {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()

  const { mutate: linkBrand, isLoading } = useMutation({
    mutationFn: linkTCRBrand,
    onSuccess: () => {
      notification.success({
        message: 'Brand linked successfully',
        placement: 'bottomRight',
        duration: 2,
      })
      form.resetFields()
      queryClient.invalidateQueries([QUERIES.TCR.COMMUNITY_REGISTRATIONS, clientId])
    },
    onError: () => {
      notification.error({
        message: 'Failed to link brand',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const handleSubmit = (values: { brandId: string }) => {
    if (!canWrite) return
    linkBrand({ clientId, externalBrandId: values.brandId.trim() })
  }

  return (
    <Card title="Brand">
      <Form form={form} layout="inline" onFinish={handleSubmit} style={{ marginBottom: '12px' }}>
        <Form.Item name="brandId" rules={[{ required: true, message: 'Please enter brand ID' }]}>
          {canWrite ? (
            <Input placeholder="Brand ID (e.g., BRAND123)" style={{ width: 320 }} />
          ) : (
            <Tooltip title={disabledTooltip}>
              <span>
                <Input disabled placeholder="Brand ID (e.g., BRAND123)" style={{ width: 320 }} />
              </span>
            </Tooltip>
          )}
        </Form.Item>
        <Form.Item>
          {canWrite ? (
            <Button htmlType="submit" icon={<LinkOutlined />} loading={isLoading} type="primary">
              Link Brand
            </Button>
          ) : (
            <Tooltip title={disabledTooltip}>
              <span>
                <Button disabled htmlType="submit" icon={<LinkOutlined />} loading={isLoading} type="primary">
                  Link Brand
                </Button>
              </span>
            </Tooltip>
          )}
        </Form.Item>
      </Form>
      <Typography.Text type="secondary">
        Enter brand ID from the TCR dashboard to link it to this client.
      </Typography.Text>
      {brand ? (
        <Descriptions bordered column={1} labelStyle={{ width: 200 }} size="small" style={{ marginTop: '16px' }}>
          <Descriptions.Item label="ID">
            <Typography.Text code copyable>
              {brand.id}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="External ID">
            <Typography.Text code copyable>
              {brand.externalId}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={brand.status === 'ACTIVE' ? 'green' : 'orange'}>{brand.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Display Name">{brand.displayName}</Descriptions.Item>
          <Descriptions.Item label="Company Name">{brand.companyName}</Descriptions.Item>
          <Descriptions.Item label="Brand Name">{brand.brandName ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="First Name">{brand.firstName ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Last Name">{brand.lastName ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Entity Type">{brand.entityType ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Email">{brand.email ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Phone">{brand.phone ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Mobile Phone">{brand.mobilePhone ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Vertical">{brand.vertical ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Universal EIN">
            {brand.universalEin ? (
              <Typography.Text code copyable>
                {brand.universalEin}
              </Typography.Text>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Inserted At">{brand.insertedAt}</Descriptions.Item>
        </Descriptions>
      ) : (
        <Empty description="No brand linked" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: '16px' }} />
      )}
    </Card>
  )
}
